<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal px-4 md:px-6 py-3">
        <div class="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div class="max-w-3xl">
            <h1 class="list-page-title">我的提醒</h1>
            <p class="mt-2 text-sm leading-6 text-slate-600">查看分配给自己的提醒任务，支持延期提醒、标记完成和回到提醒定义页快速调整规则。</p>
          </div>
        </div>
      </div>
    </template>

    <template #filters>
      <PageFilterBar :show-refresh="true" :refresh-loading="loading" @refresh="fetchList">
        <el-select v-model="filters.bizType" class="filter-bar-field-sm" clearable placeholder="选择业务类型">
          <el-option label="自定义提醒" value="CUSTOM_REMINDER" />
        </el-select>

        <template #actions>
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-slate-300 hover:text-slate-900"
            @click="openDefinitionModal"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M18.586 2.586a2 2 0 112.828 2.828L12 14.828l-4 1 1-4 9.586-9.242z" />
            </svg>
            我的定义
          </button>
        </template>

        <template #searchActions>
          <button type="button" class="btn-primary inline-flex items-center gap-2" @click="handleSearch">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            查询任务
          </button>
          <button type="button" class="btn-secondary" @click="handleReset">重置</button>
        </template>
      </PageFilterBar>
    </template>

    <div v-if="loading" class="flex items-center justify-center py-16">
      <svg class="h-8 w-8 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
    </div>

    <div v-else-if="list.length" class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      <article
        v-for="item in list"
        :key="item.id"
        class="group flex h-full flex-col overflow-hidden rounded-[24px] border border-slate-200/90 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.06)] transition-all duration-200 hover:-translate-y-1 hover:border-blue-200 hover:shadow-[0_18px_40px_rgba(37,99,235,0.12)]"
      >
        <div class="border-b border-slate-100 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.12),transparent_34%),linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] px-5 py-4">
          <div class="flex items-start justify-between gap-4">
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <span :class="['status-badge', taskStatusClass(item.status)]" class="font-semibold">{{ item.statusDesc || item.status }}</span>
                <span v-if="item.taskNo" class="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-500">
                  #{{ item.taskNo }}
                </span>
              </div>
              <h3 class="mt-3 line-clamp-2 min-h-[3.5rem] text-base font-semibold leading-7 text-slate-900">{{ item.title || '未命名提醒任务' }}</h3>
            </div>
          </div>
        </div>

        <div class="flex flex-1 flex-col px-5 py-5">
          <p class="line-clamp-2 min-h-[3rem] text-sm leading-6 text-slate-600">{{ item.content || '暂无提醒内容' }}</p>
          <div class="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <svg class="h-4 w-4 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.25a8.25 8.25 0 0115 0" />
            </svg>
            <span class="truncate">发起人：{{ formatReminderCreator(item) }}</span>
          </div>

          <div class="mt-5 grid grid-cols-3 gap-2.5">
            <div class="rounded-2xl border border-slate-100 bg-slate-50/80 px-3 py-3">
              <p class="text-xs text-slate-500">首次提醒</p>
              <p class="mt-2 text-sm font-semibold leading-5 text-slate-800">{{ formatDateTime(item.firstSendTime) || '-' }}</p>
            </div>
            <div class="rounded-2xl border border-slate-100 bg-slate-50/80 px-3 py-3">
              <p class="text-xs text-slate-500">下次提醒</p>
              <p class="mt-2 text-sm font-semibold leading-5 text-slate-800">{{ formatDateTime(item.nextSendTime) || '暂无' }}</p>
            </div>
            <div class="rounded-2xl border border-orange-100 bg-orange-50/90 px-3 py-3">
              <p class="text-xs text-orange-500">发送次数</p>
              <p class="mt-2 text-lg font-semibold leading-5 text-orange-600">{{ item.sendCount || 0 }}</p>
            </div>
          </div>
        </div>

        <div class="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-4">
          <div class="text-xs text-slate-400">
            {{ formatDateTime(item.nextSendTime || item.firstSendTime) || '-' }}
          </div>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="btn-primary min-w-[104px]"
              :disabled="isTaskProcessDisabled(item)"
              @click="handleProcessTask(item)"
            >
              处理
            </button>
          </div>
        </div>
      </article>
    </div>

    <div v-else class="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white py-16 shadow-sm">
      <div class="flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 text-slate-400">
        <svg class="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-3-3v6m9 1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h4l2-2h8a2 2 0 012 2v11z" />
        </svg>
      </div>
      <p class="mt-5 text-sm text-slate-500">当前没有可展示的提醒任务</p>
      <button type="button" class="btn-primary mt-5" @click="openDefinitionModal">前往创建提醒</button>
    </div>

    <AppPagination
      v-if="pagination.pages > 1"
      class="pagination-bridge"
      :pagination="pagination"
      :page-sizes="[10, 20, 50]"
      :show-page-count="true"
      @page-change="handlePageChange"
      @size-change="handlePageSizeChange"
    />

    <div v-if="definitionModalVisible" class="fixed inset-0 z-50 overflow-y-auto" @click.self="closeDefinitionModal">
      <div class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" @click="closeDefinitionModal"></div>
      <div class="relative flex min-h-screen items-center justify-center p-4 md:p-6">
        <div class="relative flex h-[min(900px,calc(100vh-2rem))] w-full max-w-[1400px] flex-col overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-[0_32px_90px_rgba(15,23,42,0.24)]">
          <div class="flex items-start justify-between gap-4 border-b border-slate-200 bg-[linear-gradient(135deg,#f8fbff_0%,#ffffff_60%,#f8fafc_100%)] px-6 py-5">
            <div>
              <h2 class="text-xl font-semibold text-slate-900">提醒定义</h2>
              <p class="mt-1 text-sm text-slate-500">在当前页面直接维护你的提醒定义，无需跳转离开任务列表。</p>
            </div>
            <button
              type="button"
              class="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900"
              @click="closeDefinitionModal"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="flex-1 overflow-y-auto bg-slate-50/60 px-4 py-4 md:px-6 md:py-6">
            <ReminderDefinitionList embedded />
          </div>
        </div>
      </div>
    </div>
  </ListPageShell>
</template>

<script setup>
import { getCurrentInstance, onMounted, reactive, ref } from 'vue'
// import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import ReminderDefinitionList from '@/views/reminder/ReminderDefinitionList.vue'
import { getMyReminderTaskPage } from '@/api/reminder'
import { useReminderToast } from '@/composables/useReminderToast'
import { chooseReminderTaskAction, reminderTaskActionMessage } from '@/utils/reminderTaskProcess'

// const router = useRouter()
const { proxy } = getCurrentInstance()
const { isReminderSuppressed, processReminder } = useReminderToast()

const loading = ref(false)
const list = ref([])
const definitionModalVisible = ref(false)

const filters = reactive({
  bizType: 'CUSTOM_REMINDER'
})

const pagination = ref({
  current: 1,
  size: 10,
  total: 0,
  pages: 0
})

function formatDateTime(value) {
  if (!value) return ''
  if (typeof proxy?.$formatDateTime === 'function') {
    return proxy.$formatDateTime(value)
  }
  return String(value).replace('T', ' ')
}

function taskStatusClass(status) {
  if (status === 'HANDLED' || status === 'CLOSED') return 'bg-emerald-50 text-emerald-700'
  if (status === 'WAITING_RESPONSE' || status === 'WAITING') return 'bg-blue-50 text-blue-700'
  if (status === 'EXPIRED') return 'bg-rose-50 text-rose-700'
  return 'bg-amber-50 text-amber-700'
}

function isTaskProcessDisabled(item) {
  return item?.status === 'HANDLED' || item?.status === 'CANCELLED' || item?.status === 'CLOSED'
}

function formatReminderCreator(item) {
  if (item?.creatorName) return item.creatorName
  if (item?.creatorId) return `ID: ${item.creatorId}`
  return '未知发起人'
}

async function fetchList() {
  loading.value = true
  try {
    const result = await getMyReminderTaskPage({
      bizType: filters.bizType || undefined,
      pageNum: pagination.value.current,
      pageSize: pagination.value.size
    })
    list.value = (result?.records || []).filter(item => !isReminderSuppressed(item))
    pagination.value = {
      current: result?.current || pagination.value.current,
      size: result?.size || pagination.value.size,
      total: result?.total || 0,
      pages: result?.pages || 0
    }
  } catch (error) {
    ElMessage.error('获取提醒任务失败')
    console.error('获取提醒任务失败', error)
  } finally {
    loading.value = false
  }
}

function handleSearch() {
  pagination.value.current = 1
  fetchList()
}

function handleReset() {
  filters.bizType = 'CUSTOM_REMINDER'
  pagination.value.current = 1
  fetchList()
}

function handlePageChange(page) {
  pagination.value.current = page
  fetchList()
}

function handlePageSizeChange(size) {
  pagination.value.size = size
  pagination.value.current = 1
  fetchList()
}

function openDefinitionModal() {
  definitionModalVisible.value = true
}

function closeDefinitionModal() {
  definitionModalVisible.value = false
}

// function viewDefinition(definitionId) {
//   if (!definitionId) {
//     ElMessage.warning('该任务未关联提醒定义')
//     return
//   }
//   router.push(`/reminders/definitions?edit=${definitionId}`)
// }

async function handleProcessTask(item) {
  try {
    const result = await chooseReminderTaskAction(item)
    await processReminder(item.id, result.action, item, result)
    ElMessage.success(reminderTaskActionMessage(result.action))
    await fetchList()
  } catch (error) {
    if (error !== 'cancel' && error !== 'close') {
      ElMessage.error('处理提醒失败')
      console.error('处理提醒失败', error)
    }
  }
}

onMounted(() => {
  fetchList()
})
</script>
